import { Loading } from "@components/LoadingIndicator";
import { UserList } from "@components/UserListCard";
import styled from "@emotion/styled";
import React, { useContext, useCallback, useState } from "react";

import { UserResponse } from "@every.org/common/src/codecs/entities";

import { useLoggedInUserOrUndefined } from "src/context/AuthContext/hooks";
import { UsersContext } from "src/context/UsersContext";
import {
  fetchFollowsForUser,
  FetchFollowsResult,
} from "src/context/UsersContext/actions";
import { getUser } from "src/context/UsersContext/selectors";
import { useAsyncEffect } from "src/hooks/useAsyncEffect";
import { colorCssVars } from "src/theme/color";
import { logger } from "src/utility/logger";

const LightSubText = styled.h4`
  color: var(${colorCssVars.text.secondary});
`;

interface Props {
  userId: UserResponse["id"];
  onClick?: () => void;
}

export const FollowingList: React.FCC<Props> = ({ userId, onClick }) => {
  const usersState = useContext(UsersContext);
  const loggedInUser = useLoggedInUserOrUndefined();
  const [followingIds, setFollowingIds] = useState<UserResponse["id"][]>();
  const fetch = useCallback(() => {
    return fetchFollowsForUser(userId);
  }, [userId]);

  const handleResponse = useCallback((result: FetchFollowsResult) => {
    setFollowingIds(result.followingIds);
  }, []);

  const handleError = useCallback((e: Error) => {
    logger.error({
      message: "An error occurred fetching following.",
      error: e,
    });
  }, []);

  useAsyncEffect({
    asyncOperation: fetch,
    handleResponse,
    handleError,
  });

  if (followingIds === undefined) {
    return <Loading />;
  }
  if (followingIds.length <= 0) {
    return (
      <LightSubText>
        {loggedInUser?.id === userId
          ? "Find someone to follow!"
          : "No public follows."}
      </LightSubText>
    );
  }
  const followingUsers = followingIds
    .map((followingId) => getUser(usersState, { id: followingId }))
    .filter((user) => user) as UserResponse[];
  return (
    <UserList
      usersInfo={followingUsers.map((user) => ({ user }))}
      showLocation
      onClick={onClick}
    />
  );
};
